import type { SiteName } from '@seek/melways-sites';
import {
  IconExperience,
  IconRecommended,
  IconRocket,
  IconSearch,
  Strong,
  Text,
} from 'braid-design-system';

export const jobAdFeatureChartData = (site: SiteName) => {
  switch (site) {
    case 'employer-seek-hk':
      return JOB_AD_FEATURE_CHART_HK_SITE_CONFIG;

    default:
      return JOB_AD_FEATURE_CHART_SITE_CONFIG;
  }
};

const JOB_AD_FEATURE_CHART_SITE_CONFIG: JobAdFeatureChartSiteConfig = {
  headerPlan: [
    {
      label: 'Basic',
      icon: <IconRecommended tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          Offers a balance between performance and cost with exposure to
          relevant candidates - ideal for <Strong>easy-to-fill roles</Strong>
        </Text>
      ),
      text: 'Offers a balance between performance and cost with exposure to relevant candidates - ideal for easy-to-fill roles',
    },
    {
      label: 'Advanced',
      icon: <IconRocket tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          Offers a balance between performance and cost with exposure to
          relevant candidates - ideal for <Strong>easy-to-fill roles</Strong>
        </Text>
      ),
      text: 'Offers a balance between performance and cost with exposure to relevant candidates - ideal for easy-to-fill roles',
    },
    {
      label: 'Premium',
      icon: <IconExperience tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          Our top-performing ad, with maximum exposure and features to help you
          stand out - designed for{' '}
          <Strong>hard-to-fill, critical and urgent roles</Strong>
        </Text>
      ),
      text: 'Our top-performing ad, with maximum exposure and features to help you stand out - designed for hard-to-fill, critical and urgent roles.',
    },
  ],
  jobAdPlanInfo: [
    {
      title: 'Exposure in search and recommendations',
      data: [
        {
          type: 'badge',
          info: 'Standard',
        },
        {
          type: 'badge',
          info: 'Promoted',
        },
        {
          type: 'badge',
          info: 'Prioritised',
        },
      ],
    },
    {
      title: 'Candidate invitations',
      data: [
        {
          type: 'badge',
          info: '20',
        },
        {
          type: 'badge',
          info: '30',
        },
        {
          type: 'badge',
          info: '80',
        },
      ],
    },
    {
      title: 'Enhanced targeting',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'yes',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
    {
      title: 'Promoted on competitive ads',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'yes',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
    {
      title: 'Maintain exclusivity on your ad',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
    {
      title: 'Your ad sent exclusively to high-fit candidates',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
    {
      title: 'Branding',
      data: [
        {
          type: 'icon',
          info: 'optional',
        },
        {
          type: 'icon',
          info: 'optional',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
  ],
  colNum: 4,
};

const JOB_AD_FEATURE_CHART_HK_SITE_CONFIG: JobAdFeatureChartSiteConfig = {
  headerPlan: [
    {
      label: 'Lite',
      icon: <IconSearch tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          Appears below paid ads in search results - for hiring where{' '}
          <Strong>cost-efficiency matters most</Strong>
        </Text>
      ),
      text: 'Appears below paid ads in search results - for hiring where cost-efficiency matters most',
    },
    {
      label: 'Basic',
      icon: <IconRecommended tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          Standard visibility in search and recommendations - for{' '}
          <Strong>general hiring needs</Strong>
        </Text>
      ),
      text: 'Standard visibility in search and recommendations - for general hiring needs',
    },
    {
      label: 'Advanced',
      icon: <IconRocket tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          Enhanced targeting to high fit candidates - for{' '}
          <Strong>hard-to-fill and competitive roles</Strong>
        </Text>
      ),
      text: 'Enhanced targeting to high fit candidates - for hard-to-fill and competitive roles',
    },
    {
      label: 'Premium',
      icon: <IconExperience tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          Maximum performance and premium features - for{' '}
          <Strong>urgent, critical and high-impact hiring</Strong>
        </Text>
      ),
      text: 'Maximum performance and premium features - for urgent, critical and high-impact hiring',
    },
  ],
  jobAdPlanInfo: [
    {
      title: 'Exposure in search and recommendations',
      data: [
        {
          type: 'badge',
          info: 'Limited',
        },
        {
          type: 'badge',
          info: 'Standard',
        },
        {
          type: 'badge',
          info: 'Promoted',
        },
        {
          type: 'badge',
          info: 'Prioritised',
        },
      ],
    },
    {
      title: 'Candidate invitations',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'badge',
          info: '20',
        },
        {
          type: 'badge',
          info: '30',
        },
        {
          type: 'badge',
          info: '80',
        },
      ],
    },
    {
      title: 'Enhanced targeting',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'yes',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
    {
      title: 'Promoted on competitive ads',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'yes',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
    {
      title: 'Maintain exclusivity on your ad',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
    {
      title: 'Your ad sent exclusively to high-fit candidates',
      data: [
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'no',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
    {
      title: 'Branding',
      data: [
        {
          type: 'icon',
          info: 'optional',
        },
        {
          type: 'icon',
          info: 'optional',
        },
        {
          type: 'icon',
          info: 'optional',
        },
        {
          type: 'icon',
          info: 'yes',
        },
      ],
    },
  ],
  colNum: 5,
};
export interface JobAdFeatureChartSiteConfig {
  headerPlan: Array<{
    label: string;
    icon: React.ReactNode;
    textSpan: React.ReactNode;
    text: string;
  }>;
  jobAdPlanInfo: Array<{
    title: string;
    data: JobAdPlanDataItem[];
  }>;
  colNum: 4 | 5;
}

export type JobAdPlanDataItem =
  | {
      type: 'badge';
      info: string | number;
    }
  | {
      type: 'icon';
      info: 'no' | 'yes' | 'optional';
    };
